import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Alert, Switch, FormControlLabel, FormControl,
  InputLabel, Select, MenuItem
} from '@mui/material';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

const UsersPage = () => {
  const { currentUser } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch companies');

      const data = await response.json();
      setCompanies(data.companies);

      // Auto-select first company if available
      if (data.companies.length > 0) {
        setSelectedCompanyId(data.companies[0].id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const fetchUsers = useCallback(async (companyId) => {
    try {
      setUsersLoading(true);
      setError(null);
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/companies/${companyId}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch users');

      const data = await response.json();
      setUsers(data.users);
    } catch (err) {
      setError(err.message);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchUsers(selectedCompanyId);
    } else {
      setUsers([]);
    }
  }, [selectedCompanyId, fetchUsers]);

  const handleAdminToggle = async (userId, isAdmin) => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_company_admin: isAdmin })
      });

      if (!response.ok) throw new Error('Failed to update user');

      // Refresh users list
      if (selectedCompanyId) {
        fetchUsers(selectedCompanyId);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCompanyChange = (event) => {
    setSelectedCompanyId(event.target.value);
  };

  if (loading) {
    return (
      <AdminLayout title="Users" requireSuperAdmin>
        <CircularProgress />
      </AdminLayout>
    );
  }

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  return (
    <AdminLayout title="Users" requireSuperAdmin>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel id="company-select-label">Company</InputLabel>
          <Select
            labelId="company-select-label"
            id="company-select"
            value={selectedCompanyId}
            label="Company"
            onChange={handleCompanyChange}
          >
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {usersLoading ? (
        <CircularProgress />
      ) : selectedCompanyId ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Company Admin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No users found for this company
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.name || 'Unknown'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{selectedCompany?.name || selectedCompanyId}</TableCell>
                    <TableCell>
                      {user.is_super_admin && (
                        <Chip label="Super Admin" color="primary" size="small" sx={{ mr: 1 }} />
                      )}
                      {user.is_company_admin && (
                        <Chip label="Company Admin" color="secondary" size="small" />
                      )}
                      {!user.is_super_admin && !user.is_company_admin && (
                        <Chip label="User" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={user.is_company_admin}
                            onChange={(e) => handleAdminToggle(user.id, e.target.checked)}
                            disabled={user.is_super_admin}
                          />
                        }
                        label=""
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">Select a company to view its users</Alert>
      )}
    </AdminLayout>
  );
};

export default UsersPage;
